import { useCallback, useEffect, useRef, useState } from "react";
import { SearchX } from "lucide-react";
import { AppHeader } from "./components/AppHeader/AppHeader";
import {
  BottomNavigation,
  type NavigationTab,
} from "./components/BottomNavigation/BottomNavigation";
import { FeaturedCarousel } from "./components/FeaturedCarousel/FeaturedCarousel";
import { ActivityView } from "./components/ActivityView/ActivityView";
import {
  FriendsView,
  type LoginProvider,
} from "./components/FriendsView/FriendsView";
import { GameDetailDialog } from "./components/GameDetailDialog/GameDetailDialog";
import { GameHistoryView } from "./components/GameHistoryView/GameHistoryView";
import { GameSession } from "./components/GameSession/GameSession";
import { GamesView } from "./components/GamesView/GamesView";
import { RecommendedGameCard } from "./components/RecommendedGameCard/RecommendedGameCard";
import { RecommendedGames } from "./components/RecommendedGames/RecommendedGames";
import { SavedGamesView } from "./components/SavedGamesView/SavedGamesView";
import {
  SettingsView,
  type PartySettings,
} from "./components/SettingsView/SettingsView";
import {
  SideMenu,
  type SecondaryPage,
} from "./components/SideMenu/SideMenu";
import { games, type Game } from "./data/games";
import { isGameSimulationSupported } from "./game-engine/gameDefinitions";
import styles from "./App.module.css";

export function App() {
  const [searchValue, setSearchValue] = useState("");
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [activeSessionGame, setActiveSessionGame] = useState<Game | null>(null);
  const [activeTab, setActiveTab] = useState<NavigationTab>("discover");
  const [activePage, setActivePage] = useState<SecondaryPage | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [savedGameIds, setSavedGameIds] = useState<readonly string[]>([
    "quiz-night",
    "who-in-the-room",
    "truth-or-dare",
  ]);
  const [settings, setSettings] = useState<PartySettings>({
    sound: true,
    vibration: true,
    celebrations: true,
    reminders: false,
    theme: "system",
  });
  const [announcement, setAnnouncement] = useState("");
  const openerRef = useRef<HTMLButtonElement | null>(null);
  const menuOpenerRef = useRef<HTMLButtonElement | null>(null);

  const normalizedSearch = searchValue.trim().toLocaleLowerCase("sv-SE");
  const filteredGames = normalizedSearch
    ? games.filter((game) =>
        [game.title, game.category, game.description].some((value) =>
          value.toLocaleLowerCase("sv-SE").includes(normalizedSearch),
        ),
      )
    : games;

  const featuredGames = games.filter((game) => game.featured);
  const recommendedGames = games.filter((game) => game.recommended);
  const savedGames = games.filter((game) => savedGameIds.includes(game.id));

  useEffect(() => {
    const root = document.documentElement;

    if (settings.theme === "system") {
      delete root.dataset.theme;
    } else {
      root.dataset.theme = settings.theme;
    }

    return () => {
      delete root.dataset.theme;
    };
  }, [settings.theme]);

  const handleSelectGame = (
    game: Game,
    trigger: HTMLButtonElement,
  ) => {
    openerRef.current = trigger;
    setSelectedGame(game);
  };

  const handleCloseDialog = useCallback(() => {
    setSelectedGame(null);
    requestAnimationFrame(() => openerRef.current?.focus());
  }, []);

  const handleStartGame = (game: Game) => {
    setSelectedGame(null);
    setActiveSessionGame(game);
  };

  const handleChooseDemo = (gameId: string) => {
    const demoGame = games.find((game) => game.id === gameId);

    if (demoGame) {
      handleStartGame(demoGame);
    }
  };

  const handleExitSession = () => {
    setActiveSessionGame(null);
    requestAnimationFrame(() => document.getElementById("game-search")?.focus());
  };

  const handleOpenMenu = (trigger: HTMLButtonElement) => {
    menuOpenerRef.current = trigger;
    setIsMenuOpen(true);
  };

  const handleCloseMenu = useCallback(() => {
    setIsMenuOpen(false);
    requestAnimationFrame(() => menuOpenerRef.current?.focus());
  }, []);

  const handleTabChange = (tab: NavigationTab) => {
    setActivePage(null);
    setActiveTab(tab);
    const tabName =
      tab === "games"
        ? "Spel"
        : tab === "discover"
          ? "Upptäck"
          : tab === "activity"
            ? "Aktivitet"
            : "Vänner";
    setAnnouncement(`${tabName} är vald.`);
  };

  const handleOpenPage = (page: SecondaryPage) => {
    setActivePage(page);
    const pageName =
      page === "saved"
        ? "Sparade spel"
        : page === "history"
          ? "Spelhistorik"
          : "Inställningar";
    setAnnouncement(`${pageName} är öppen.`);
  };

  const handleBackFromPage = () => {
    setActivePage(null);
  };

  const handleBrowseGames = () => {
    setActivePage(null);
    handleTabChange("games");
  };

  const handleRemoveSavedGame = (game: Game) => {
    setSavedGameIds((current) =>
      current.filter((gameId) => gameId !== game.id),
    );
    setAnnouncement(`${game.title} har tagits bort från sparade spel.`);
  };

  const handleSettingChange = <Key extends keyof PartySettings>(
    key: Key,
    value: PartySettings[Key],
  ) => {
    setSettings((current) => ({ ...current, [key]: value }));
    setAnnouncement("Inställningen har uppdaterats.");
  };

  const handleResetSettings = () => {
    setSettings({
      sound: true,
      vibration: true,
      celebrations: true,
      reminders: false,
      theme: "system",
    });
    setAnnouncement("Standardinställningarna har återställts.");
  };

  const handleLogin = (provider: LoginProvider) => {
    setAnnouncement(
      `Fortsätt med ${provider}. Inloggningen öppnas när kontotjänsten är ansluten.`,
    );
  };

  const showSearchResults = normalizedSearch.length > 0;

  if (activeSessionGame) {
    return (
      <GameSession game={activeSessionGame} onExit={handleExitSession} />
    );
  }

  return (
    <div className={styles.app}>
      <div
        className={styles.shell}
        aria-hidden={selectedGame || isMenuOpen ? "true" : undefined}
        inert={selectedGame !== null || isMenuOpen}
      >
        {activePage === "saved" ? (
          <SavedGamesView
            games={savedGames}
            onBack={handleBackFromPage}
            onBrowseGames={handleBrowseGames}
            onMenuOpen={handleOpenMenu}
            onRemove={handleRemoveSavedGame}
            onSelectGame={handleSelectGame}
          />
        ) : activePage === "history" ? (
          <GameHistoryView
            games={games}
            onBack={handleBackFromPage}
            onBrowseGames={handleBrowseGames}
            onMenuOpen={handleOpenMenu}
            onSelectGame={handleSelectGame}
          />
        ) : activePage === "settings" ? (
          <SettingsView
            settings={settings}
            onBack={handleBackFromPage}
            onMenuOpen={handleOpenMenu}
            onReset={handleResetSettings}
            onSettingChange={handleSettingChange}
            onShowMessage={setAnnouncement}
          />
        ) : activeTab === "discover" || activeTab === "games" ? (
          <>
            <AppHeader
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onMenuOpen={handleOpenMenu}
              onShowSaved={() => handleOpenPage("saved")}
            />

            {showSearchResults ? (
              <main className={styles.main}>
                <h1 className="visually-hidden">
                  {activeTab === "games"
                    ? "Sök bland spel i PartyCast"
                    : "Sök bland upptäckter i PartyCast"}
                </h1>
                <section
                  className={styles.searchSection}
                  aria-labelledby="search-results-heading"
                >
                  <div className={styles.searchHeading}>
                    <span className={styles.eyebrow}>Direkt sökning</span>
                    <h2 id="search-results-heading">Sökresultat</h2>
                    {filteredGames.length > 0 ? (
                      <p>{filteredGames.length} spel matchar</p>
                    ) : null}
                  </div>

                  {filteredGames.length > 0 ? (
                    <div className={styles.resultsGrid}>
                      {filteredGames.map((game) => (
                        <RecommendedGameCard
                          game={game}
                          key={game.id}
                          onSelect={handleSelectGame}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      <span className={styles.emptyIcon}>
                        <SearchX aria-hidden="true" />
                      </span>
                      <h3>Inga spel hittades</h3>
                      <p>Prova ett annat sökord eller utforska alla spel.</p>
                      <button type="button" onClick={() => setSearchValue("")}>
                        Rensa sökning
                      </button>
                    </div>
                  )}
                </section>
              </main>
            ) : activeTab === "games" ? (
              <GamesView games={games} onSelectGame={handleSelectGame} />
            ) : (
              <main className={styles.main}>
                <h1 className="visually-hidden">Upptäck spel i PartyCast</h1>

                <>
                  <FeaturedCarousel
                    games={featuredGames}
                    onSelectGame={handleSelectGame}
                  />
                  <RecommendedGames
                    games={recommendedGames}
                    onSelectGame={handleSelectGame}
                  />
                </>
              </main>
            )}
          </>
        ) : activeTab === "activity" ? (
          <ActivityView
            onShowFriends={() => handleTabChange("friends")}
            onStartPlaying={() => handleTabChange("discover")}
          />
        ) : (
          <FriendsView onLogin={handleLogin} />
        )}

        {activePage === null ? (
          <BottomNavigation
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        ) : null}
        <p className="visually-hidden" aria-live="polite" aria-atomic="true">
          {announcement}
        </p>
      </div>

      {isMenuOpen ? (
        <SideMenu
          activePage={activePage}
          activeTab={activeTab}
          onClose={handleCloseMenu}
          onNavigate={handleTabChange}
          onOpenPage={handleOpenPage}
          onShowMessage={setAnnouncement}
        />
      ) : null}

      {selectedGame ? (
        <GameDetailDialog
          game={selectedGame}
          simulationAvailable={isGameSimulationSupported(selectedGame.id)}
          onClose={handleCloseDialog}
          onStartGame={handleStartGame}
          onChooseDemo={handleChooseDemo}
        />
      ) : null}
    </div>
  );
}

export default App;
